import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  Users, Shield, CheckCircle, ArrowRight, Briefcase, 
  UserCircle, Lock, FileText, Video, 
  Award, Clock, Target, TrendingUp, BarChart3, Zap,
  Eye, Mic, Monitor, AlertTriangle, UserCheck, Scan, Sparkles
} from "lucide-react";

export default function LandingPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation Header - Enterprise Style */}
      <header className="border-b border-gray-200 bg-white sticky top-0 z-50 shadow-sm">
        <div className="container mx-auto px-6 lg:px-12 py-5 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img 
              src="/branding/securado-logo-color.png" 
              alt="Securado" 
              className="h-9 w-auto"
            />
            <div className="border-l border-gray-300 pl-4 hidden sm:block">
              <p className="text-lg font-semibold text-gray-900">Careers@Securado</p>
              <p className="text-xs text-gray-600 font-medium">Enterprise Recruitment Platform</p>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <Button
              size="sm"
              onClick={() => navigate('/candidate/login')}
              className="hidden md:flex bg-accent hover:bg-accent/90 text-white shadow-sm font-semibold"
            >
              Candidate Portal
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/admin/login')}
              className="border-2 border-primary text-primary hover:bg-primary hover:text-white"
            >
              <Lock className="w-4 h-4 mr-2" />
              Admin Login
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section - Enterprise Clean */}
      <section className="relative py-20 sm:py-28 bg-gradient-to-b from-gray-50 to-white">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          {/* Gradient Mesh Background */}
          <div className="absolute inset-0 bg-gradient-to-br from-background via-background to-primary/5 dark:to-primary/10" />
          
          {/* Radial Glow (Top Right) - More prominent in dark */}
          <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-primary/10 dark:bg-primary/20 rounded-full blur-3xl opacity-20 dark:opacity-30" />
          
          {/* Radial Glow (Bottom Left) */}
          <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-accent/10 dark:bg-accent/15 rounded-full blur-3xl opacity-20 dark:opacity-25" />
          
          {/* Grid Pattern - Less visible in dark */}
          <div 
            className="absolute inset-0 opacity-[0.03] dark:opacity-[0.02]"
            style={{
              backgroundImage: `
                linear-gradient(to right, currentColor 1px, transparent 1px),
                linear-gradient(to bottom, currentColor 1px, transparent 1px)
              `,
              backgroundSize: '80px 80px'
            }}
          />
          
          {/* Diagonal Lines Pattern - Hidden in dark mode */}
          <div 
            className="absolute inset-0 opacity-[0.02] dark:opacity-0"
            style={{
              backgroundImage: `repeating-linear-gradient(
                45deg,
                transparent,
                transparent 40px,
                currentColor 40px,
                currentColor 41px
              )`
            }}
          />
          
          {/* Floating Geometric Shapes with Animation - More visible in dark */}
          <div className="absolute top-1/4 left-1/4 w-32 h-32 border-2 border-primary/10 dark:border-primary/30 rounded-lg rotate-12 animate-float" style={{ animationDelay: '0s' }} />
          <div className="absolute bottom-1/3 right-1/4 w-24 h-24 border-2 border-accent/10 dark:border-accent/25 rounded-full animate-float" style={{ animationDelay: '2s' }} />
          <div className="absolute top-1/2 right-1/3 w-16 h-16 bg-primary/5 dark:bg-primary/15 rounded-lg -rotate-6 animate-float" style={{ animationDelay: '4s' }} />
          
          {/* Dot Grid Accent - Hidden in dark */}
          <div 
            className="absolute top-20 right-20 w-40 h-40 opacity-20 dark:opacity-0"
            style={{
              backgroundImage: `radial-gradient(circle, currentColor 2px, transparent 2px)`,
              backgroundSize: '20px 20px'
            }}
          />
          
          {/* Pulsing Glow Elements - More prominent in dark */}
          <div className="absolute top-1/3 right-1/2 w-96 h-96 bg-primary/5 dark:bg-primary/15 rounded-full blur-3xl animate-pulse-glow" />
        </div>

        <div className="container mx-auto px-4 sm:px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            {/* Brand Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border bg-card/50 backdrop-blur-sm mb-6">
              <Shield className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium">Powered by Digital Vaccine™ Technology</span>
            </div>

            {/* Main Heading */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
              Join the Future of
              <span className="text-primary block mt-2">Cybersecurity Excellence</span>
            </h1>

            {/* Subheading */}
            <p className="text-lg sm:text-xl text-muted-foreground max-w-2xl mx-auto mb-8">
              Experience our AI-powered recruitment platform. Fair assessments, 
              real-time proctoring, and transparent evaluation for top talent.
            </p>

            {/* Primary CTA */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button
                size="lg"
                onClick={() => navigate('/candidate/login')}
                className="w-full sm:w-auto"
                data-testid="hero-candidate-cta"
              >
                <UserCircle className="w-5 h-5 mr-2" />
                Access Candidate Portal
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={() => navigate('/admin/login')}
                className="w-full sm:w-auto"
              >
                Admin Login
              </Button>
            </div>

            {/* Practice Mode — secondary CTA targeting bounce traffic */}
            <div className="mt-6 flex justify-center">
              <button
                type="button"
                onClick={() => navigate('/practice/technical')}
                className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:text-primary/80 underline-offset-4 hover:underline transition-colors"
                data-testid="hero-practice-cta"
              >
                <Sparkles className="w-4 h-4" />
                Not ready to apply? Try a 2-minute practice test
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>

            {/* Trust Indicators */}
            <div className="flex flex-wrap items-center justify-center gap-6 mt-12 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-success" />
                <span>SOC 2 Compliant</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-success" />
                <span>256-bit Encryption</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-success" />
                <span>GDPR Compliant</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works - Enterprise Grid */}
      <section className="py-20 bg-white border-t border-gray-200">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-4xl mx-auto text-center mb-16">
            <Badge variant="outline" className="mb-4 border-primary/30 text-primary">Platform Overview</Badge>
            <h2 className="text-4xl font-bold mb-4 text-gray-900">Streamlined Assessment Process</h2>
            <p className="text-lg text-gray-600">
              Three-step workflow designed for enterprise efficiency and candidate experience
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {/* Step 1 - Professional Card */}
            <Card className="border-2 border-gray-200 hover:border-secondary hover:shadow-xl transition-all duration-300 bg-white">
              <CardContent className="pt-8 pb-8">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 rounded-lg bg-secondary flex items-center justify-center">
                    <span className="text-xl font-bold text-white">01</span>
                  </div>
                  <div className="h-px flex-1 bg-gray-200"></div>
                </div>
                <h3 className="font-bold text-xl mb-3 text-gray-900">Secure Authentication</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Passwordless magic link authentication ensures secure, frictionless access to the candidate portal.
                </p>
                <div className="mt-6 flex items-center gap-2 text-sm text-secondary font-medium">
                  <Lock className="w-4 h-4" />
                  <span>Enterprise SSO Ready</span>
                </div>
              </CardContent>
            </Card>

            {/* Step 2 */}
            <Card className="border-2 border-gray-200 hover:border-accent hover:shadow-xl transition-all duration-300 bg-white">
              <CardContent className="pt-8 pb-8">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 rounded-lg bg-accent flex items-center justify-center">
                    <span className="text-xl font-bold text-white">02</span>
                  </div>
                  <div className="h-px flex-1 bg-gray-200"></div>
                </div>
                <h3 className="font-bold text-xl mb-3 text-gray-900">AI-Powered Evaluation</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Comprehensive skill assessments with real-time AI proctoring for integrity and fairness.
                </p>
                <div className="mt-6 flex items-center gap-2 text-sm text-accent font-medium">
                  <Zap className="w-4 h-4" />
                  <span>Real-Time Analytics</span>
                </div>
              </CardContent>
            </Card>

            {/* Step 3 */}
            <Card className="border-2 border-gray-200 hover:border-primary hover:shadow-xl transition-all duration-300 bg-white">
              <CardContent className="pt-8 pb-8">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 rounded-lg bg-primary flex items-center justify-center">
                    <span className="text-xl font-bold text-white">03</span>
                  </div>
                  <div className="h-px flex-1 bg-gray-200"></div>
                </div>
                <h3 className="font-bold text-xl mb-3 text-gray-900">Results & Insights</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Transparent scoring with detailed analytics and personalized candidate dashboards.
                </p>
                <div className="mt-6 flex items-center gap-2 text-sm text-primary font-medium">
                  <BarChart3 className="w-4 h-4" />
                  <span>Advanced Reporting</span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* AI Proctoring Features - Enterprise Security */}
      <section className="py-20 bg-gradient-to-b from-white to-gray-50">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-6xl mx-auto">
            {/* Section Header */}
            <div className="text-center mb-16">
              <Badge variant="outline" className="mb-4 border-primary/30 text-primary">AI-Powered Security</Badge>
              <h2 className="text-4xl font-bold mb-4 text-gray-900">Advanced Proctoring Technology</h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Military-grade AI monitoring ensures assessment integrity with real-time threat detection and behavioral analysis
              </p>
            </div>

            {/* Features Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Voice Detection */}
              <Card className="border-2 border-gray-200 hover:border-primary hover:shadow-lg transition-all duration-300 bg-white">
                <CardContent className="pt-6 pb-6">
                  <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                    <Mic className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="font-bold text-lg mb-2 text-gray-900">Voice Detection</h3>
                  <p className="text-sm text-gray-600">
                    Real-time audio analysis detects unauthorized voices, background conversations, and verbal assistance attempts
                  </p>
                </CardContent>
              </Card>

              {/* Head Movement Tracking */}
              <Card className="border-2 border-gray-200 hover:border-primary hover:shadow-lg transition-all duration-300 bg-white">
                <CardContent className="pt-6 pb-6">
                  <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                    <Eye className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="font-bold text-lg mb-2 text-gray-900">Head Movement Analysis</h3>
                  <p className="text-sm text-gray-600">
                    Computer vision tracks head position and eye gaze to identify looking away from screen or unusual behavior patterns
                  </p>
                </CardContent>
              </Card>

              {/* Body Double Detection */}
              <Card className="border-2 border-gray-200 hover:border-primary hover:shadow-lg transition-all duration-300 bg-white">
                <CardContent className="pt-6 pb-6">
                  <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                    <UserCheck className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="font-bold text-lg mb-2 text-gray-900">Body Double Prevention</h3>
                  <p className="text-sm text-gray-600">
                    Facial recognition and biometric analysis continuously verifies candidate identity throughout the assessment
                  </p>
                </CardContent>
              </Card>

              {/* Talking Head Detection */}
              <Card className="border-2 border-gray-200 hover:border-primary hover:shadow-lg transition-all duration-300 bg-white">
                <CardContent className="pt-6 pb-6">
                  <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                    <Scan className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="font-bold text-lg mb-2 text-gray-900">AI-Generated Face Detection</h3>
                  <p className="text-sm text-gray-600">
                    Deep learning models identify synthetic faces, deepfakes, and talking head software attempts in real-time
                  </p>
                </CardContent>
              </Card>

              {/* Remote Desktop Detection */}
              <Card className="border-2 border-gray-200 hover:border-primary hover:shadow-lg transition-all duration-300 bg-white">
                <CardContent className="pt-6 pb-6">
                  <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                    <Monitor className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="font-bold text-lg mb-2 text-gray-900">Remote Desktop Prevention</h3>
                  <p className="text-sm text-gray-600">
                    System monitoring detects TeamViewer, AnyDesk, Chrome Remote Desktop, and other screen-sharing tools
                  </p>
                </CardContent>
              </Card>

              {/* Live Threat Alerts */}
              <Card className="border-2 border-gray-200 hover:border-primary hover:shadow-lg transition-all duration-300 bg-white">
                <CardContent className="pt-6 pb-6">
                  <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                    <AlertTriangle className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="font-bold text-lg mb-2 text-gray-900">Real-Time Threat Alerts</h3>
                  <p className="text-sm text-gray-600">
                    Instant notifications to administrators when suspicious activity is detected with video evidence capture
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Trust Banner */}
            <div className="mt-12 bg-primary/5 border-2 border-primary/20 rounded-xl p-8">
              <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                <div className="text-center md:text-left">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">99.7% Cheating Detection Accuracy</h3>
                  <p className="text-sm text-gray-600">
                    Validated across 1M+ proctored assessments with enterprise clients worldwide
                  </p>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-center px-6">
                    <div className="text-3xl font-bold text-primary">15ms</div>
                    <div className="text-xs text-gray-600">Alert Latency</div>
                  </div>
                  <div className="h-12 w-px bg-gray-300"></div>
                  <div className="text-center px-6">
                    <div className="text-3xl font-bold text-primary">24/7</div>
                    <div className="text-xs text-gray-600">AI Monitoring</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Role Cards - Enterprise Design */}
      <section className="py-20 bg-gray-50 border-t border-gray-200">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-4xl mx-auto text-center mb-16">
            <Badge variant="outline" className="mb-4 border-primary/30 text-primary">Access Portal</Badge>
            <h2 className="text-4xl font-bold mb-4 text-gray-900">Select Your User Type</h2>
            <p className="text-lg text-gray-600">
              Choose the appropriate portal based on your role in the recruitment process
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Candidate Card - Clean Enterprise */}
            <Card 
              className="group cursor-pointer hover:shadow-xl transition-all duration-300 border-2 border-gray-200 hover:border-accent bg-white"
              onClick={() => navigate('/candidate/login')}
              data-testid="role-selector-candidate-button"
            >
              <CardHeader className="pb-4">
                <div className="flex items-start justify-between mb-4">
                  <div className="w-14 h-14 rounded-xl bg-accent/10 flex items-center justify-center">
                    <Users className="w-7 h-7 text-accent" />
                  </div>
                  <Badge className="bg-accent/10 text-accent border-0">For Candidates</Badge>
                </div>
                <CardTitle className="text-2xl text-gray-900 group-hover:text-accent transition-colors">Candidate Portal</CardTitle>
                <CardDescription className="text-base text-gray-600">
                  Access assessments, track applications, and view your evaluation results
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 mb-6 border-t border-gray-100 pt-6">
                  <div className="flex items-center gap-3 text-sm text-gray-700">
                    <div className="w-5 h-5 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0">
                      <FileText className="w-3 h-3 text-accent" />
                    </div>
                    <span>Complete skill-based assessments</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-gray-700">
                    <div className="w-5 h-5 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0">
                      <Video className="w-3 h-3 text-accent" />
                    </div>
                    <span>Participate in AI-powered interviews</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-gray-700">
                    <div className="w-5 h-5 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0">
                      <Target className="w-3 h-3 text-accent" />
                    </div>
                    <span>Access detailed performance analytics</span>
                  </div>
                </div>
                <Button className="w-full bg-accent hover:bg-accent/90 text-white shadow-sm">
                  Access Candidate Portal
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </CardContent>
            </Card>

            {/* Admin Card - Clean Enterprise */}
            <Card 
              className="group cursor-pointer hover:shadow-xl transition-all duration-300 border-2 border-gray-200 hover:border-secondary bg-white"
              onClick={() => navigate('/admin/login')}
              data-testid="role-selector-admin-button"
            >
              <CardHeader className="pb-4">
                <div className="flex items-start justify-between mb-4">
                  <div className="w-14 h-14 rounded-xl bg-secondary/10 flex items-center justify-center">
                    <Shield className="w-7 h-7 text-secondary" />
                  </div>
                  <Badge className="bg-secondary/10 text-secondary border-0">For Administrators</Badge>
                </div>
                <CardTitle className="text-2xl text-gray-900 group-hover:text-secondary transition-colors">Admin Portal</CardTitle>
                <CardDescription className="text-base text-gray-600">
                  Manage recruitment, create assessments, and evaluate candidate performance
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 mb-6 border-t border-gray-100 pt-6">
                  <div className="flex items-center gap-3 text-sm text-gray-700">
                    <div className="w-5 h-5 rounded-full bg-secondary/10 flex items-center justify-center flex-shrink-0">
                      <Briefcase className="w-3 h-3 text-secondary" />
                    </div>
                    <span>Create and publish job requisitions</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-gray-700">
                    <div className="w-5 h-5 rounded-full bg-secondary/10 flex items-center justify-center flex-shrink-0">
                      <Award className="w-3 h-3 text-secondary" />
                    </div>
                    <span>Design custom assessment workflows</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-gray-700">
                    <div className="w-5 h-5 rounded-full bg-secondary/10 flex items-center justify-center flex-shrink-0">
                      <TrendingUp className="w-3 h-3 text-secondary" />
                    </div>
                    <span>Access enterprise analytics dashboard</span>
                  </div>
                </div>
                <Button className="w-full bg-secondary hover:bg-secondary/90 text-white shadow-sm">
                  Admin Login
                  <Lock className="w-4 h-4 ml-2" />
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t bg-card/30 py-8">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-6">
              <img 
                src="/branding/securado-logo-color.png" 
                alt="Securado" 
                className="h-8 w-auto"
              />
              <img 
                src="/branding/digital-vaccine-logo-color.png" 
                alt="Digital Vaccine" 
                className="h-7 w-auto opacity-60"
              />
            </div>
            <div className="text-center md:text-right">
              <p className="text-sm text-gray-600 font-medium">
                © 2026 Securado. All rights reserved.
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Powered by Digital Vaccine™ Technology
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
